import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CapTablesService } from 'api';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  AggregatedFundOwnershipDetail,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/WaterfallFundSummaryRowConfig.types';
import {
  DEFAULT_COL_FOR_KEY,
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/WaterfallFundSummaryParser/WaterfallFundSummaryParser.constants';
import {
  UseWaterfallIrrParams,
  UseWaterfallIrrResult,
} from './useWaterfallIrr.props';

export type { UseWaterfallIrrParams, UseWaterfallIrrResult } from './useWaterfallIrr.props';

export const useWaterfallIrr = ({
  workbookCells,
  selectedCaptable,
  exitDate,
  onChange,
  setFindIrrMap,
  isFirmPage,
  companyList,
  isFinancialsLoading,
}: UseWaterfallIrrParams): UseWaterfallIrrResult => {
  const irrCache = useRef<{ [key: string]: Promise<number | null> }>({});

  // Check if inputs are loaded and ready for IRR calculation
  const isInputsLoaded = useMemo(() => {
    if (isFirmPage) {
      return !!companyList?.length && !isFinancialsLoading;
    }
    return !isFinancialsLoading;
  }, [isFirmPage, companyList, isFinancialsLoading]);

  // Fetch IRR data with caching
  const getIrrData = useCallback(
    async (captableId: number, fundId: number, unrealizedValue: number, exit: string) => {
      const cacheKey = `${captableId}-${fundId}-${unrealizedValue}-${exit}`;

      if (cacheKey in irrCache.current) {
        return irrCache.current[cacheKey];
      }

      const promise = CapTablesService.apiCapTablesGrossIrrRead(
        captableId,
        fundId,
        unrealizedValue.toString(),
        exit
      )
        .then(response => response.gross_irr)
        .catch(error => {
          delete irrCache.current[cacheKey];
          throw error;
        });

      irrCache.current[cacheKey] = promise;
      return promise;
    },
    []
  );

  // Fetch and update IRR values when inputs are ready
  useEffect(() => {
    if (workbookCells?.waterfallFundSummary && selectedCaptable && exitDate && isInputsLoaded) {
      const waterfallFundSummaryCells = Object.values(
        workbookCells.waterfallFundSummary ?? {}
      ) as EvaluatedCell<AggregatedFundOwnershipDetail>[];

      const equityValueCells = waterfallFundSummaryCells.filter(
        cell => cell.alias === 'exitProceeds' && cell.value !== 0
      );

      if (equityValueCells.every(cell => !cell.invalidExpr)) {
        equityValueCells.forEach(cell => {
          getIrrData(
            selectedCaptable,
            cell.data?.fund as number,
            cell.value as number,
            exitDate
          ).then(grossIrr => {
            const relatedCell =
              workbookCells.waterfallFundSummary[DEFAULT_COL_FOR_KEY.irr + cell.rowNumber];
            if (relatedCell) {
              setFindIrrMap(prev => {
                if (cell.data?.fund && prev[cell.data.fund] !== grossIrr) {
                  return { ...prev, [relatedCell.rowNumber]: grossIrr as number ?? 0 };
                }
                return prev;
              });
              onChange(relatedCell, grossIrr);
            }
          });
        });
      }
    }
  }, [workbookCells, selectedCaptable, onChange, exitDate, getIrrData, isInputsLoaded, setFindIrrMap]);

  return {
    isInputsLoaded,
  };
};
